import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Copy, Download, Check, Lock, ArrowRight, Sparkles, Heart } from "lucide-react";
import useSubscription from "@/hooks/useSubscription";
import SubscribedBadge from "@/components/earthpulse/SubscribedBadge";
import { getSubscriberEmail } from "@/lib/api";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
// Discreet, ad-hoc donation URL — Stripe payment link OR Ko-fi. Falls back
// to a soft-encouragement text with no link if none is configured.
const DONATE_URL = process.env.REACT_APP_DONATE_URL || "https://ko-fi.com/earthpulse";

/**
 * CalendarSync — a members-only "Sync your celestial calendar" perk that
 * hands the user three one-tap ways to add Earth Pulse's celestial guidance
 * (moon phases, solstices/equinoxes, meteor showers, comets, eclipses,
 * Mercury retrograde windows) to their phone or laptop calendar.
 *
 *   • Google Calendar → https://calendar.google.com/calendar/u/0/r?cid=<webcal-url>
 *   • Apple / Outlook / Fastmail → webcal:// URL (auto-subscribes)
 *   • Copy raw ICS URL (for anything that takes a URL feed)
 *   • Download static .ics file (one-shot import; won't auto-update)
 *
 * Non-subscribers see a compact locked teaser panel matching SkyEventsToWatch.
 */
export default function CalendarSync() {
  const { active: subActive, loading: subLoading } = useSubscription();

  const [email, setEmail] = useState(getSubscriberEmail() || "");
  const [copied, setCopied] = useState(false);

  const feedUrls = useMemo(() => {
    if (!email) return null;
    const enc = encodeURIComponent(email);
    const httpsUrl = `${API}/api/calendar/celestial.ics?email=${enc}`;
    const webcalUrl = httpsUrl.replace(/^https:\/\//i, "webcal://");
    const googleUrl = `https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(webcalUrl)}`;
    const downloadUrl = `${API}/api/calendar/celestial.ics?email=${enc}&download=1`;
    return { httpsUrl, webcalUrl, googleUrl, downloadUrl };
  }, [email]);

  const copyUrl = async () => {
    if (!feedUrls) return;
    try {
      await navigator.clipboard.writeText(feedUrls.httpsUrl);
      setCopied(true);
      toast.success("Feed URL copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Could not copy — long-press the URL to select it manually.");
    }
  };

  // ── Non-subscriber locked teaser ─────────────────────────────
  if (!subLoading && !subActive) {
    return (
      <div
        className="parchment-card rounded-2xl p-8 h-full flex flex-col relative overflow-hidden"
        data-testid="calendar-sync-panel-locked"
      >
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            background:
              "radial-gradient(circle at 80% 20%, var(--gold), transparent 40%), radial-gradient(circle at 20% 70%, var(--rose-gold), transparent 40%)",
          }}
        />
        <div className="relative flex items-center justify-between mb-4 gap-4">
          <div className="label-overline flex items-center gap-2 constellation-underline">
            <Calendar size={12} /> Sync to your calendar
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[color:var(--gold)]/40 bg-[color:var(--gold)]/10 text-[10px] font-medium tracking-wider uppercase text-[color:var(--gold)]">
            <Lock size={10} /> Members
          </span>
        </div>

        <div className="relative flex-1 flex flex-col justify-center items-center text-center py-8 md:py-14">
          <div className="mx-auto w-14 h-14 rounded-full border border-[color:var(--gold)]/40 flex items-center justify-center">
            <Calendar size={20} className="text-[color:var(--gold)]" />
          </div>
          <h3 className="mt-6 font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl tracking-tight max-w-md">
            The whole cosmic year, <span className="font-serif-italic text-[color:var(--gold)]">inside your calendar</span>.
          </h3>
          <p className="mt-4 font-serif-italic text-[color:var(--parchment-soft)] text-sm max-w-md leading-relaxed">
            One tap adds every new moon, full moon, meteor shower, comet, eclipse and Mercury-retrograde window to Google, Apple, Outlook or Fastmail — auto-updates as the year unfolds.
          </p>
          <Link
            to="/subscribe"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition"
            data-testid="calendar-sync-subscribe"
          >
            <Sparkles size={14} /> Unlock with membership · £8.88 / mo <ArrowRight size={14} />
          </Link>
          <p className="mt-4 font-serif-italic text-[color:var(--text-muted)] text-xs">
            Included with your Earth Pulse membership · cancel anytime.
          </p>
        </div>
      </div>
    );
  }

  // ── Subscriber view ─────────────────────────────────────────
  return (
    <div className="parchment-card rounded-2xl p-8 h-full flex flex-col" data-testid="calendar-sync-panel">
      <div className="flex items-center justify-between mb-4 gap-4">
        <div className="label-overline flex items-center gap-2 constellation-underline">
          <Calendar size={12} /> Sync to your calendar
          <SubscribedBadge />
        </div>
      </div>

      <p className="font-serif-italic text-[color:var(--text-muted)] text-xs mb-5">
        Auto-updating feed — every new moon, meteor shower, comet, eclipse and Mercury-retrograde window.
      </p>

      {!email && (
        <div className="mb-4">
          <label htmlFor="cal-email" className="label-overline text-[color:var(--gold)] text-[10px] block mb-2">
            Your membership email
          </label>
          <input
            id="cal-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full bg-white border border-slate-300 rounded-lg h-10 px-3 text-slate-900 font-sans-body focus:outline-none focus:border-[color:var(--gold)] focus:ring-2 focus:ring-[color:var(--gold)]/40"
            data-testid="calendar-sync-email-input"
          />
        </div>
      )}

      {feedUrls && (
        <div className="space-y-3" data-testid="calendar-sync-buttons">
          <a
            href={feedUrls.googleUrl}
            target="_blank"
            rel="noreferrer"
            className="w-full inline-flex items-center justify-center gap-3 px-5 py-3 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition"
            data-testid="calendar-sync-google"
          >
            <Calendar size={14} /> Add to Google Calendar
          </a>
          <a
            href={feedUrls.webcalUrl}
            className="w-full inline-flex items-center justify-center gap-3 px-5 py-3 rounded-full border border-[color:var(--gold)]/50 text-[color:var(--parchment)] hover:bg-[color:var(--gold)]/10 font-medium transition"
            data-testid="calendar-sync-apple"
          >
            <Calendar size={14} /> Apple · Outlook · Fastmail
          </a>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={copyUrl}
              className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-full border border-[color:var(--gold)]/30 text-[color:var(--parchment-soft)] hover:text-[color:var(--parchment)] hover:border-[color:var(--gold)]/60 text-sm transition"
              data-testid="calendar-sync-copy"
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? "Copied" : "Copy URL"}
            </button>
            <a
              href={feedUrls.downloadUrl}
              className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-full border border-[color:var(--gold)]/30 text-[color:var(--parchment-soft)] hover:text-[color:var(--parchment)] hover:border-[color:var(--gold)]/60 text-sm transition"
              data-testid="calendar-sync-download"
              download="earthpulse-celestial.ics"
            >
              <Download size={13} /> .ics file
            </a>
          </div>
        </div>
      )}

      {/* Discreet donation nudge — small, warm, unobtrusive */}
      <div className="mt-auto pt-6">
        <div className="border-t border-[color:var(--gold)]/15 pt-4 flex items-start gap-3">
          <Heart size={12} className="mt-0.5 text-[color:var(--rose-gold)] shrink-0" />
          <div className="font-serif-italic text-[color:var(--text-muted)] text-xs leading-relaxed">
            If Earth Pulse becomes part of your rhythm, {" "}
            <a
              href={DONATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="calendar-sync-donate"
              className="text-[color:var(--gold)] hover:text-[color:var(--rose-gold)] underline decoration-[color:var(--gold)]/30 hover:decoration-[color:var(--rose-gold)] underline-offset-2 transition"
            >
              a small tip
            </a>
            {" "} keeps it here for the next soul who needs it.
          </div>
        </div>
      </div>
    </div>
  );
}
